import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChevronDown,
  Eye,
  EyeOff,
  Frame,
  MoreVertical,
  Pencil,
  Plus,
  Square,
  type LucideIcon,
} from "lucide-react";
import type { SocialPostSceneBlockV1 } from "@runcheetah/shared";
import { Button, Input, Slider } from "@runcheetah/ui";

import { getLayerMetaLabel } from "./campaign-creative-layer-display";
import type { CreativeLayerEntry } from "./campaign-creative-layer-ordering";
import {
  canRenameLayer,
  getLayerSwatchColor,
  getLayerSwatchInitials,
} from "./creative-layer-editor-utils";

type CreativeLayerAddMenuItem = {
  label: string;
  icon: LucideIcon;
  onClick?: () => void;
  children?: Array<{ label: string; icon: LucideIcon; onClick: () => void }>;
};

type CreativeLayerSortableRowProps = {
  entry: CreativeLayerEntry;
  isActive: boolean;
  isTransformActive: boolean;
  layerLabel: string;
  editableLayerName: string;
  renameAriaLabel: string;
  reorderAriaLabel: string;
  hideAriaLabel: string;
  showAriaLabel: string;
  onSelectBlock: (blockId: string, options?: { toggle?: boolean }) => void;
  onRenameBlock: (blockId: string, layerName: string) => void;
  onToggleVisibility: (blockId: string, visible: boolean) => void;
};

type CreativeInspectorNumberFieldProps = {
  label: string;
  value: number;
  onCommit: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
};

type CreativeRectangleSizePresetControlProps = {
  width: number;
  height: number;
  onUpdate: (changes: { width: number; height: number }) => void;
  squareLabel: string;
  rectangleLabel: string;
};

type CreativeInspectorSelectFieldProps = {
  label: string;
  value: string;
  options: string[];
  previewOptions?: boolean;
  onChange: (value: string) => void;
};

type CreativeInspectorSliderFieldProps = {
  label: string;
  value: number;
  valueLabel: string;
  min: number;
  max: number;
  step: number;
  onCommit: (value: number) => void;
};

type CreativeHashtagInputProps = {
  hashtags: string[];
  placeholder: string;
  addLabel: ReactNode;
  onChange: (hashtags: string[]) => void;
};

type CreativeInspectorSectionProps = {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  headerRight?: ReactNode;
};

export function CreativeInspectorLabel({ children }: { children: ReactNode }) {
  return (
    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground/48">
      {children}
    </div>
  );
}

export function CreativeInspectorSection({
  title,
  children,
  defaultOpen = false,
  headerRight,
}: CreativeInspectorSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="min-w-0 overflow-hidden rounded-3xl border border-border/70 bg-background/82 shadow-[0_10px_26px_rgba(19,34,49,0.045)]">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/45"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground/58">
          {title}
        </span>
        <span className="flex items-center gap-2">
          {headerRight ? (
            <span onClick={(event) => event.stopPropagation()}>{headerRight}</span>
          ) : null}
          <ChevronDown
            className={[
              "h-3.5 w-3.5 text-foreground/38 transition-transform duration-150",
              open ? "rotate-180" : "",
            ].join(" ")}
            aria-hidden="true"
          />
        </span>
      </button>
      {open ? (
        <div className="min-w-0 border-t border-border/50 bg-[#fffdf8]/60 px-4 pb-4 pt-3">
          {children}
        </div>
      ) : null}
    </div>
  );
}

export function CreativeInspectorSelectField({
  label,
  value,
  options,
  previewOptions = false,
  onChange,
}: CreativeInspectorSelectFieldProps) {
  return (
    <div className="min-w-0 space-y-1.5">
      <CreativeInspectorLabel>{label}</CreativeInspectorLabel>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-11 w-full rounded-2xl border border-border/70 bg-[#f7f4eb] px-3 text-sm font-medium text-foreground outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] focus:border-foreground/30"
      >
        {options.map((option) => (
          <option
            key={option}
            value={option}
            style={previewOptions ? { fontFamily: option } : undefined}
          >
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

export function CreativeInspectorSliderField({
  label,
  value,
  valueLabel,
  min,
  max,
  step,
  onCommit,
}: CreativeInspectorSliderFieldProps) {
  return (
    <div className="min-w-0 space-y-2 rounded-2xl border border-border/50 bg-[#f7f4eb]/70 px-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <CreativeInspectorLabel>{label}</CreativeInspectorLabel>
        <span className="rounded-full bg-background px-2 py-0.5 text-[11px] font-semibold tabular-nums text-foreground/62 shadow-sm">
          {valueLabel}
        </span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={([nextValue]) => {
          if (typeof nextValue === "number") {
            onCommit(nextValue);
          }
        }}
        onValueCommit={([nextValue]) => {
          if (typeof nextValue === "number") {
            onCommit(nextValue);
          }
        }}
      />
    </div>
  );
}

export function CreativeHashtagInput({
  hashtags,
  placeholder,
  addLabel,
  onChange,
}: CreativeHashtagInputProps) {
  const [value, setValue] = useState("");
  const commit = () => {
    const trimmed = value.trim().replace(/^#+/, "");
    if (trimmed) {
      onChange([...hashtags, trimmed]);
      setValue("");
    }
  };

  return (
    <div className="flex gap-1.5">
      <Input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit();
          }
        }}
        className="h-8 rounded-full text-sm"
        placeholder={placeholder}
      />
      <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={commit}>
        {addLabel}
      </Button>
    </div>
  );
}

export function CreativeLayerAddMenu({
  disabled,
  items,
  label,
  compact = false,
  align = "left",
}: {
  disabled: boolean;
  items: CreativeLayerAddMenuItem[];
  label: string;
  compact?: boolean;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={
          compact
            ? "flex h-7 w-7 items-center justify-center rounded-full border border-border/70 bg-background text-foreground/60 shadow-xs transition hover:bg-muted/70 hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
            : "flex items-center gap-2 rounded-full border border-border/70 bg-background px-3 py-2 text-sm text-foreground shadow-xs transition hover:bg-muted/70 disabled:pointer-events-none disabled:opacity-50"
        }
        title={label}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        {!compact ? label : null}
      </button>
      {open ? (
        <div
          className={[
            "absolute top-[calc(100%+0.5rem)] z-10 min-w-40 rounded-2xl border border-border/70 bg-card p-1.5 shadow-lg",
            align === "right" ? "right-0" : "left-0",
          ].join(" ")}
        >
          {items.map((item) =>
            item.children ? (
              <div key={item.label} className="rounded-xl px-2 py-1.5">
                <div className="mb-1 flex items-center gap-2 px-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground/42">
                  <item.icon className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>{item.label}</span>
                </div>
                <div className="grid gap-1">
                  {item.children.map((child) => (
                    <button
                      key={child.label}
                      type="button"
                      className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-foreground transition hover:bg-muted/70"
                      onClick={() => {
                        child.onClick();
                        setOpen(false);
                      }}
                    >
                      <child.icon className="h-4 w-4 text-foreground/55" aria-hidden="true" />
                      <span>{child.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <button
                key={item.label}
                type="button"
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-foreground transition hover:bg-muted/70"
                onClick={() => {
                  item.onClick?.();
                  setOpen(false);
                }}
              >
                <item.icon className="h-4 w-4 text-foreground/55" aria-hidden="true" />
                <span>{item.label}</span>
              </button>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

function CreativeLayerSwatch({ block }: { block: SocialPostSceneBlockV1 }) {
  const color = getLayerSwatchColor(block);

  if (color) {
    return (
      <span
        className="h-8 w-8 shrink-0 rounded-xl border border-border/70 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.28),inset_0_-10px_18px_rgba(44,44,42,0.08)]"
        style={{ background: color }}
        aria-hidden="true"
      />
    );
  }

  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-border/70 bg-background/70 text-[10px] font-semibold uppercase tracking-[0.04em] text-foreground/48 shadow-[inset_0_1px_0_rgba(255,255,255,0.72)]">
      {getLayerSwatchInitials(block)}
    </span>
  );
}

export function CreativeLayerSortableRow({
  entry,
  isActive,
  isTransformActive,
  layerLabel,
  editableLayerName,
  renameAriaLabel,
  reorderAriaLabel,
  hideAriaLabel,
  showAriaLabel,
  onSelectBlock,
  onRenameBlock,
  onToggleVisibility,
}: CreativeLayerSortableRowProps) {
  const block = entry.block;
  const canRename = canRenameLayer(block);
  const [isRenaming, setIsRenaming] = useState(false);
  const [draftLayerName, setDraftLayerName] = useState(layerLabel);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });

  return (
    <div
      ref={setNodeRef}
      role="button"
      tabIndex={0}
      onClick={(event) => onSelectBlock(block.id, { toggle: event.metaKey || event.ctrlKey })}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelectBlock(block.id, { toggle: event.metaKey || event.ctrlKey });
        }
      }}
      className={[
        "group/layer relative grid w-full min-w-0 cursor-pointer grid-cols-[1.5rem_2rem_minmax(0,1fr)_1.75rem] items-center gap-2 rounded-2xl border px-2.5 py-2 text-left transition-[background-color,border-color,box-shadow,opacity] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/35",
        isActive
          ? "border-accent/25 bg-accent/[0.075] shadow-[inset_2px_0_0_var(--accent)]"
          : isTransformActive
            ? "border-accent/20 bg-accent/[0.045]"
            : "border-transparent bg-transparent hover:border-border/60 hover:bg-background/62",
        !block.visible ? "opacity-55" : "",
        isDragging ? "relative z-10 opacity-80 shadow-deep" : "",
      ].join(" ")}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <span
        className="flex h-8 w-6 cursor-grab items-center justify-center rounded-lg text-foreground/28 outline-none transition-colors hover:bg-muted/80 hover:text-foreground/60 active:cursor-grabbing group-hover/layer:text-foreground/50"
        aria-label={reorderAriaLabel}
        {...attributes}
        {...listeners}
      >
        <MoreVertical className="h-4 w-4" aria-hidden="true" />
      </span>
      <CreativeLayerSwatch block={block} />
      <div className="min-w-0 leading-tight">
        {isRenaming ? (
          <Input
            autoFocus
            value={draftLayerName}
            className="h-7 rounded-lg px-2 text-[13px] font-medium"
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => setDraftLayerName(event.target.value)}
            onBlur={() => {
              const nextName = draftLayerName.trim();
              if (nextName) {
                onRenameBlock(block.id, nextName);
              } else {
                setDraftLayerName(layerLabel);
              }
              setIsRenaming(false);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.currentTarget.blur();
              }
              if (event.key === "Escape") {
                setDraftLayerName(layerLabel);
                setIsRenaming(false);
              }
            }}
          />
        ) : (
          <div className="flex min-w-0 items-center gap-1.5">
            <div
              className="truncate text-[13px] font-medium tracking-[-0.01em] text-foreground"
              title={layerLabel}
            >
              {layerLabel}
            </div>
            {canRename ? (
              <button
                type="button"
                className="shrink-0 rounded-full p-1 text-foreground/28 opacity-0 transition hover:bg-muted/80 hover:text-foreground/65 group-hover/layer:opacity-100"
                aria-label={renameAriaLabel}
                onClick={(event) => {
                  event.stopPropagation();
                  setDraftLayerName(editableLayerName);
                  setIsRenaming(true);
                }}
              >
                <Pencil className="h-3 w-3" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        )}
        <div className="mt-1 truncate text-[11px] font-normal text-foreground/45">
          {getLayerMetaLabel({ entry, isTransformActive })}
        </div>
      </div>
      <button
        type="button"
        className="flex h-8 w-7 shrink-0 items-center justify-center rounded-full text-foreground/38 transition-colors hover:bg-muted/80 hover:text-foreground group-hover/layer:text-foreground/55"
        aria-label={block.visible ? hideAriaLabel : showAriaLabel}
        aria-pressed={block.visible}
        onClick={(event) => {
          event.stopPropagation();
          onToggleVisibility(block.id, !block.visible);
        }}
      >
        {block.visible ? (
          <Eye className="h-4 w-4" aria-hidden="true" />
        ) : (
          <EyeOff className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}

export function CreativeRectangleSizePresetControl({
  width,
  height,
  onUpdate,
  squareLabel,
  rectangleLabel,
}: CreativeRectangleSizePresetControlProps) {
  return (
    <div className="grid grid-cols-2 gap-1.5 rounded-2xl border border-border/60 bg-muted/25 p-1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 justify-center rounded-xl text-xs font-semibold"
        disabled={width === height}
        onClick={() => {
          const side = Math.max(8, Math.round(Math.min(width, height)));
          onUpdate({ width: side, height: side });
        }}
      >
        <Square className="h-3.5 w-3.5" aria-hidden="true" />
        {squareLabel}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 justify-center rounded-xl text-xs font-semibold"
        disabled={width > height}
        onClick={() => {
          const nextHeight = Math.max(8, Math.round(height));
          onUpdate({ width: Math.round(nextHeight * 1.5), height: nextHeight });
        }}
      >
        <Frame className="h-3.5 w-3.5" aria-hidden="true" />
        {rectangleLabel}
      </Button>
    </div>
  );
}

export function CreativeInspectorNumberField({
  label,
  value,
  onCommit,
  min,
  max,
  step = 1,
}: CreativeInspectorNumberFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [draftValue, setDraftValue] = useState(String(value));

  useEffect(() => {
    if (document.activeElement === inputRef.current) {
      return;
    }

    setDraftValue(String(value));
  }, [value]);

  const commitDraftValue = (nextValue: string, options?: { normalize?: boolean }) => {
    if (!nextValue.trim()) {
      if (options?.normalize) {
        setDraftValue(String(value));
      }
      return;
    }

    const parsedValue = Number(nextValue);

    if (!Number.isFinite(parsedValue)) {
      if (options?.normalize) {
        setDraftValue(String(value));
      }
      return;
    }

    const committedValue = Math.min(max ?? parsedValue, Math.max(min ?? parsedValue, parsedValue));
    if (options?.normalize) {
      setDraftValue(String(committedValue));
    }
    if (committedValue !== value) {
      onCommit(committedValue);
    }
  };

  return (
    <div className="min-w-0">
      <div className="space-y-1.5 rounded-2xl border border-border/65 bg-[#f7f4eb] px-3 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
        <span className="block truncate text-[10px] font-semibold uppercase tracking-[0.1em] text-foreground/42">
          {label}
        </span>
        <Input
          ref={inputRef}
          type="number"
          value={draftValue}
          min={min}
          max={max}
          step={step}
          onChange={(event) => {
            const nextValue = event.target.value;
            setDraftValue(nextValue);
            commitDraftValue(nextValue);
          }}
          onBlur={() => commitDraftValue(draftValue, { normalize: true })}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              commitDraftValue(draftValue, { normalize: true });
            }
          }}
          className="h-8 min-w-0 rounded-xl border border-border/55 bg-background/70 px-2.5 text-left text-sm font-semibold tabular-nums shadow-none focus-visible:ring-1 focus-visible:ring-foreground/20"
        />
      </div>
    </div>
  );
}
